import type {RentalAssetType} from '../assets/rental-assets/schemas'
import type {WorkerType} from '../assets/worker-types/schemas'
import type {JobRequirementValue} from './types'

export function requirementSlotKey(requirement: JobRequirementValue): string | null {
    if (requirement.requirementType === 'worker') {
        return requirement.workerTypeId
    }

    return requirement.rentalAssetTypeId
}

export type AvailableRequirementSlots = {
    availableWorkerTypeIds: string[]
    availableRentalAssetTypeIds: string[]
    canAddRequirement: boolean
}

export function getAvailableSlots(
    requirements: JobRequirementValue[],
    rentalAssetTypes: RentalAssetType[],
    workerTypes: WorkerType[],
): AvailableRequirementSlots {
    const usedKeys = new Set<string>()

    for (const requirement of requirements) {
        const key = requirementSlotKey(requirement)
        if (key != null) {
            usedKeys.add(key)
        }
    }

    const availableWorkerTypeIds = workerTypes
        .map((type) => type.id)
        .filter((typeId) => !usedKeys.has(typeId))
    const availableRentalAssetTypeIds = rentalAssetTypes
        .map((type) => type.id)
        .filter((typeId) => !usedKeys.has(typeId))
    const canAddRequirement =
        availableWorkerTypeIds.length > 0 || availableRentalAssetTypeIds.length > 0

    return {availableWorkerTypeIds, availableRentalAssetTypeIds, canAddRequirement}
}

export function getRequirementTypeOptionsForRow(
    requirements: JobRequirementValue[],
    rowIndex: number,
    rentalAssetTypes: RentalAssetType[],
    workerTypes: WorkerType[],
): Array<{label: string; value: string}> {
    const currentRequirement = requirements[rowIndex]
    const options: Array<{label: string; value: string}> = []

    const usedWorkerTypeIds = new Set<string>()
    requirements.forEach((requirement, index) => {
        if (index === rowIndex || requirement.requirementType !== 'worker') {
            return
        }

        const key = requirementSlotKey(requirement)
        if (key != null) {
            usedWorkerTypeIds.add(key)
        }
    })
    const hasUnusedWorkerType = workerTypes.some((type) => !usedWorkerTypeIds.has(type.id))
    if (currentRequirement?.requirementType === 'worker' || hasUnusedWorkerType) {
        options.push({label: 'Worker', value: 'worker'})
    }

    const usedRentalTypeIds = new Set<string>()
    requirements.forEach((requirement, index) => {
        if (index === rowIndex || requirement.requirementType !== 'rental_asset') {
            return
        }

        const key = requirementSlotKey(requirement)
        if (key != null) {
            usedRentalTypeIds.add(key)
        }
    })
    const hasUnusedRentalType = rentalAssetTypes.some((type) => !usedRentalTypeIds.has(type.id))
    if (currentRequirement?.requirementType === 'rental_asset' || hasUnusedRentalType) {
        options.push({label: 'Rental asset', value: 'rental_asset'})
    }

    return options
}

export function getWorkerTypeOptionsForRow(
    requirements: JobRequirementValue[],
    rowIndex: number,
    workerTypes: WorkerType[],
): Array<{label: string; value: string}> {
    const currentRequirement = requirements[rowIndex]
    const usedTypeIds = new Set<string>()

    requirements.forEach((requirement, index) => {
        if (index === rowIndex || requirement.requirementType !== 'worker') {
            return
        }

        const key = requirementSlotKey(requirement)
        if (key != null) {
            usedTypeIds.add(key)
        }
    })

    return workerTypes
        .filter((type) => type.id === currentRequirement?.workerTypeId || !usedTypeIds.has(type.id))
        .map((type) => ({
            label: type.name,
            value: type.id,
        }))
}

export function getRentalAssetTypeOptionsForRow(
    requirements: JobRequirementValue[],
    rowIndex: number,
    rentalAssetTypes: RentalAssetType[],
): Array<{label: string; value: string}> {
    const currentRequirement = requirements[rowIndex]
    const usedTypeIds = new Set<string>()

    requirements.forEach((requirement, index) => {
        if (index === rowIndex || requirement.requirementType !== 'rental_asset') {
            return
        }

        const key = requirementSlotKey(requirement)
        if (key != null) {
            usedTypeIds.add(key)
        }
    })

    return rentalAssetTypes
        .filter(
            (type) =>
                type.id === currentRequirement?.rentalAssetTypeId || !usedTypeIds.has(type.id),
        )
        .map((type) => ({
            label: type.name,
            value: type.id,
        }))
}

export function createRequirementForNextSlot(
    requirements: JobRequirementValue[],
    rentalAssetTypes: RentalAssetType[],
    workerTypes: WorkerType[],
): JobRequirementValue {
    const slots = getAvailableSlots(requirements, rentalAssetTypes, workerTypes)

    if (slots.availableWorkerTypeIds.length > 0) {
        return {
            requirementType: 'worker',
            quantity: 1,
            workerTypeId: slots.availableWorkerTypeIds[0],
            rentalAssetTypeId: null,
            billingRate: null,
            billingType: null,
        }
    }

    return {
        requirementType: 'rental_asset',
        quantity: 1,
        workerTypeId: null,
        rentalAssetTypeId: slots.availableRentalAssetTypeIds[0] ?? null,
        billingRate: null,
        billingType: null,
    }
}

export function hasDuplicateRequirementSlots(requirements: JobRequirementValue[]): boolean {
    const seenKeys = new Set<string>()

    for (const requirement of requirements) {
        const key = requirementSlotKey(requirement)
        if (key == null) {
            continue
        }

        if (seenKeys.has(key)) {
            return true
        }

        seenKeys.add(key)
    }

    return false
}
